import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Client, GlobalAccount, GUID, MembershipType, PersonOrg, PersonOrgType } from '@ndvr/customer-api';
import { Account, CustodyAccountLinkRequest, LifecycleStatus, Portfolio, PortfolioType } from '@ndvr/portfolio-api';

import { forgetRecentSearchEntity } from '@/components/recent-search-store';
import { CustomerApi, PortfolioApi } from 'API';
import { logger } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { ClientFormValues } from 'views/person/person-form-model';
import { createOrUpdateOrg } from 'views/person/person-form-utils';
import { Toast } from 'widgets';

export const useClientShortProfiles = () => {
    const { currentTenant } = useAuthContext();

    return useQuery({
        queryKey: ['tenant', currentTenant?.tenantGUID, 'client', 'list'],
        queryFn: async () => {
            if (!currentTenant?.tenantGUID) {
                return [];
            }
            const [response] = await CustomerApi().getClientProfilesByTenant(currentTenant.tenantGUID);
            return response ?? [];
        },
        enabled: !!currentTenant?.tenantGUID,
    });
};

export const useClientShortProfilesForTenants = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: ['tenants', tenantGUIDs, 'client', 'list'],
        queryFn: async () => {
            const [response] = await CustomerApi().getClientProfilesByTenants(tenantGUIDs);
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const useClientProfilesForHousehold = (globalAccountGUID: GUID<GlobalAccount> | undefined) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'clientProfiles'],
        queryFn: async () => {
            if (globalAccountGUID) {
                const [response] = await CustomerApi().getCommandCenterClientProfilesForHousehold(globalAccountGUID);
                return response ?? undefined;
            }
        },
    });
};

export const useClient = (clientGUID: GUID<Client> | undefined) => {
    return useQuery({
        queryKey: ['client', clientGUID],
        queryFn: async () => {
            if (clientGUID) {
                const [response] = await CustomerApi().getCommandCenterClientProfile(clientGUID);
                return response ?? undefined;
            }
        },
        enabled: !!clientGUID,
    });
};

export const useLinkPortfolio = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            portfolio,
            request,
            clientGUID,
        }: {
            portfolio: Portfolio;
            request: CustodyAccountLinkRequest;
            clientGUID: GUID<Client>;
        }) => {
            logger.info(
                `Linking portfolio ${portfolio.portfolioGUID} to custody account ${request.custodyAccountId.slice(
                    -4,
                )} and client ${clientGUID}`,
            );

            if (portfolio.portfolioType === PortfolioType.DRAFT) {
                await PortfolioApi({ hideError: true }).convertToRealPortfolio(portfolio.portfolioGUID, {
                    clientGUID: clientGUID,
                });
            }

            await PortfolioApi({ hideError: true }).linkAccountToCustodyAccount(portfolio.portfolioGUID, request);
        },
        onSuccess: (_, { clientGUID }) => {
            Toast.success('Portfolio linked');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['client', clientGUID],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to link portfolio');
        },
    });
};

export const useSaveClient = (globalAccountGUID: GUID<GlobalAccount>, clientGUID?: GUID<Client>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ClientFormValues) => {
            if (data.personOrgType === PersonOrgType.JOINT_PERSONS) {
                return handleJointSave(globalAccountGUID, data, clientGUID);
            }

            if (clientGUID) {
                return CustomerApi().updateClient(clientGUID, { startDate: data.startDate, endDate: data.endDate });
            }

            if (!data.personOrgGUID) {
                throw Error('Missing PersonOrg GUID');
            }

            return CustomerApi().createClient({
                globalAccountGUID,
                personOrgGUID: data.personOrgGUID,
                startDate: data.startDate,
            });
        },
        onSuccess: () => {
            Toast.success('Account Owner saved');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'clientProfiles'],
            });
            clientGUID &&
                queryClient.invalidateQueries({
                    queryKey: ['client', clientGUID],
                });
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to save Account Owner');
        },
    });
};

export const useDeleteClient = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (clientGUID: GUID<Client>) => {
            const [response] = await CustomerApi().deleteClient(clientGUID);
            return response ?? undefined;
        },
        onSuccess: (result, clientGUID) => {
            if (result?.valid === true) {
                forgetRecentSearchEntity(clientGUID);
            }
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'clientProfiles'],
            });
            queryClient.invalidateQueries({
                queryKey: ['client', clientGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete account-owner record'),
    });
};

const handleJointSave = async (
    globalAccountGUID: GUID<GlobalAccount>,
    data: ClientFormValues,
    clientGUID?: GUID<Client>,
) => {
    if (clientGUID) {
        await CustomerApi().updateClient(clientGUID, { startDate: data.startDate, endDate: data.endDate });
        return createOrUpdateJoint(globalAccountGUID, data);
    }

    const [accountProfile] = await CustomerApi().getCommandCenterHouseholdProfile(globalAccountGUID);
    const existingJoints = accountProfile?.personOrgFinancialProfiles.filter(
        ({ profile }) => profile.personOrg.personOrgType === PersonOrgType.JOINT_PERSONS,
    );

    let personOrgGUID;

    if (!existingJoints?.length) {
        const newJoint = await createOrUpdateJoint(globalAccountGUID, data);
        personOrgGUID = newJoint?.personOrgGUID;
    } else {
        const memberProfiles = await Promise.all(
            existingJoints?.map(({ profile }) => CustomerApi().getPersonOrgLinksForParent(profile.personOrgGUID)),
        );

        const match = existingJoints.find((_, idx) => {
            const [members] = memberProfiles[idx];
            if (!members) {
                return;
            }
            return (
                !!members.find(
                    ({ membershipType, memberPersonOrgGUID }) =>
                        membershipType === MembershipType.JOINT_PRIMARY &&
                        memberPersonOrgGUID === data.jointOwners?.JOINT_PRIMARY,
                ) &&
                !!members.find(
                    ({ membershipType, memberPersonOrgGUID }) =>
                        membershipType === MembershipType.JOINT_SECONDARY &&
                        memberPersonOrgGUID === data.jointOwners?.JOINT_SECONDARY,
                )
            );
        });

        if (match) {
            personOrgGUID = match?.profile.personOrgGUID;
        } else {
            const newJoint = await createOrUpdateJoint(globalAccountGUID, data);
            personOrgGUID = newJoint?.personOrgGUID;
        }
    }

    if (!personOrgGUID) {
        throw new Error('Failed to create joint Account Owner');
    }

    return CustomerApi().createClient({
        globalAccountGUID,
        personOrgGUID,
        startDate: data.startDate,
    });
};

const createOrUpdateJoint = async (globalAccountGUID: GUID<GlobalAccount>, data: ClientFormValues) => {
    if (!data.jointOwners) {
        throw new Error('Missing joint member');
    }

    return createOrUpdateOrg(
        { globalAccountGUID, orgGUID: data.personOrgGUID },
        {
            personOrgType: PersonOrgType.JOINT_PERSONS,
            orgName: data.orgName,
            highNetWorth: data.highNetWorth,
            members: {
                [MembershipType.JOINT_PRIMARY]: [
                    {
                        parentPersonOrgGUID: '',
                        memberPersonOrgGUID: data.jointOwners?.JOINT_PRIMARY,
                        membershipType: MembershipType.JOINT_PRIMARY,
                    },
                ],
                [MembershipType.JOINT_SECONDARY]: [
                    {
                        parentPersonOrgGUID: '',
                        memberPersonOrgGUID: data.jointOwners?.JOINT_SECONDARY,
                        membershipType: MembershipType.JOINT_SECONDARY,
                    },
                ],
            },
        },
    );
};

export const useLinkCustodyAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            account,
            request,
            clientGUID,
        }: {
            account: Account;
            request: CustodyAccountLinkRequest;
            globalAccountGUID: string;
            clientGUID: string;
        }) => {
            logger.info(
                `Linking account ${account.accountGUID} to custody account ${request.custodyAccountId.slice(-4)} `,
            );

            if (LifecycleStatus.DRAFT === account.lifecycleStatus) {
                logger.info(`Converting account ${account.accountGUID} to REAL`);
                await PortfolioApi({ hideError: true }).convertToRealPortfolio(account.accountGUID, { clientGUID });
            }

            await PortfolioApi({ hideError: true }).linkAccountToCustodyAccount(account.accountGUID, request);
        },
        onSuccess: (_data, variables) => {
            Toast.success('Account linked');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', variables.globalAccountGUID],
            });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to link account');
        },
    });
};

export const useCreateClient = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ personOrgGUID, startDate }: { personOrgGUID: GUID<PersonOrg>; startDate: string }) => {
            const [response] = await CustomerApi().createClient({ globalAccountGUID, personOrgGUID, startDate });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['globalAccount', globalAccountGUID, 'clientProfiles'] });
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to create Account Owner');
        },
    });
};

export const useGetClientsForGlobalAccount = (globalAccountGUID: string) =>
    useQuery({
        queryKey: ['clients', globalAccountGUID],
        queryFn: async () => {
            const [clients] = await CustomerApi().getClientsForGlobalAccount(globalAccountGUID);
            return clients || [];
        },
    });
